import { Flex, Text, UnstyledButton } from '@mantine/core';
import { Fragment, useCallback, useMemo } from 'react';
import { TbPlus, TbTrash } from 'react-icons/tb';
import {
  BORDER_COLOR,
  CELL_PADDING,
  FONT_SIZE,
  HEADER_BG,
  INDENT_SIZE,
  LEFT_SECTION_WIDTH,
  LINE_HEIGHT,
  TEXT_COLOR,
  useTableStyles
} from '../tableConstants';
import { parseQuestionDisplayName, pickColumnValues } from '../tableUtils';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { SeeMoreText } from '../components/SeeMoreText';
import { QuestionHeader } from '../components/QuestionHeader';
import { idCreator } from '../../../../../Project/utils/idCreator';

const renderMultiFundsSection = ({
  question,
  schema,
  funds,
  fundKeys,
  tableName,
  rowIndex,
  tableIndex,
  levelIndex,
  onAddColumn,
  onDeleteColumn,
  handleCellChange,
  totalTableCount,
  questionIndex,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  showQuestionHeader = true,
  classes
}) => {
  const { columns, children } = question;
  const columnEntries = Object.entries(columns ?? {});
  return (
    <>
      <Flex
        id={`formpf-multiple-section-row-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
        className={classes.borderBottom}
      >
        <Flex
          id={`formpf-multiple-section-left-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
          maw={LEFT_SECTION_WIDTH}
          miw={LEFT_SECTION_WIDTH}
          bg={HEADER_BG}
          justify="space-between"
          align="center"
          className={classes.leftSticky}
        >
          <Text
            id={`formpf-multiple-section-table-index-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
            c={TEXT_COLOR}
            fz={FONT_SIZE}
            lh={LINE_HEIGHT}
            p={CELL_PADDING}
            miw={INDENT_SIZE}
            maw={INDENT_SIZE}
            className={classes.borderRight}
          >
            ({tableIndex})
          </Text>
          <Flex
            align="center"
            justify="center"
            gap="10px"
            p={CELL_PADDING}
            className={classes.borderRight}
          >
            {totalTableCount > 1 && (
              <UnstyledButton
                id={`formpf-multiple-section-delete-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
                onClick={() => onDeleteColumn(tableName, rowIndex)}
                display="flex"
              >
                <TbTrash size={16} color="#228be6" />
              </UnstyledButton>
            )}
            <UnstyledButton
              id={`formpf-multiple-section-add-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
              onClick={() => onAddColumn(tableName, rowIndex)}
              display="flex"
            >
              <TbPlus size={16} color="#228be6" />
            </UnstyledButton>
          </Flex>
        </Flex>
        <Text
          id={`formpf-multiple-section-right-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
          p={CELL_PADDING}
          bg={HEADER_BG}
          flex="auto"
        />
      </Flex>

      {!children && (
        <>
          {columnEntries.map(([columnKey, columnSchema]) => (
            <MultiFundRow
              key={columnKey}
              columnSchema={columnSchema}
              cellSchema={schema.properties[columnKey]}
              levelIndex={levelIndex}
              fundKeys={fundKeys}
              getValue={fundKey => funds?.[fundKey]?.fund_data?.[tableName]?.[rowIndex] || {}}
              onFundChange={(fundKey, value) =>
                handleCellChange(columnKey, value, fundKey, rowIndex)
              }
              cellRefs={cellRefs}
              getCellKey={fundKey => `${keyPrefix}-${columnKey}-${fundKey}-${rowIndex}`}
              onCellKeyDown={onCellKeyDown}
              funds={funds}
              tableName={tableName}
              fieldName={columnKey}
              hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
              symbol={schema.properties[columnKey]?.symbol}
            />
          ))}
        </>
      )}
      {children &&
        children.map(childQuestion =>
          renderMultiFundsSection({
            question: childQuestion,
            schema,
            funds,
            fundKeys,
            tableName,
            rowIndex,
            tableIndex,
            levelIndex: levelIndex + 1,
            onAddColumn,
            onDeleteColumn,
            handleCellChange,
            totalTableCount,
            questionIndex,
            cellRefs,
            onCellKeyDown,
            keyPrefix: childQuestion.level ?? childQuestion.name,
            showQuestionHeader,
            classes
          })
        )}
    </>
  );
};

const renderSingleFundSection = ({
  question,
  schema,
  table,
  tableName,
  rowIndex,
  tableIndex,
  levelIndex,
  totalTableCount,
  onAddColumn,
  onDeleteColumn,
  handleCellChange,
  questionIndex,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  showQuestionHeader = true,
  fund
}) => {
  const { columns, children, level, name } = question;
  const columnEntries = Object.entries(columns ?? {});
  const columnValues = children
    ? {}
    : pickColumnValues(table, [
        ...columnEntries.map(([key]) => key),
        'id',
        'report_id',
        'static_template_fields',
        'errors'
      ]);

  return (
    <>
      {/* {showQuestionHeader && (
        <SingleFundHeaderRow
          levelIndex={levelIndex}
          level={level}
          nameContent={
            <Flex
              bg={HEADER_BG}
              p={CELL_PADDING}
              flex="auto"
              justify="space-between"
              align="center"
            >
              <Text id={`formpf-multiple-header-name-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`} c={TEXT_COLOR} fz={FONT_SIZE} lh={LINE_HEIGHT}>
                <SeeMoreText text={name} />
              </Text>
            </Flex>
          }
        />
      )} */}

      <SingleFundHeaderRow
        levelIndex={levelIndex}
        nameContent={
          <Flex
            id={`formpf-multiple-single-header-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
            bg={HEADER_BG}
            p={CELL_PADDING}
            flex="auto"
            justify="space-between"
            align="center"
            sx={() => ({ borderRight: `1px solid ${BORDER_COLOR}` })}
          >
            <Text
              id={`formpf-multiple-single-header-index-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
              c={TEXT_COLOR}
              fz={FONT_SIZE}
              lh={LINE_HEIGHT}
            >
              ({tableIndex})
            </Text>
            <Flex
              id={`formpf-multiple-single-header-actions-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
              align="center"
              justify="center"
              gap="10px"
            >
              {totalTableCount > 1 && (
                <UnstyledButton
                  id={`formpf-multiple-single-delete-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
                  onClick={() => onDeleteColumn(tableName, rowIndex)}
                  display="flex"
                >
                  <TbTrash size={16} color="#228be6" />
                </UnstyledButton>
              )}
              <UnstyledButton
                id={`formpf-multiple-single-add-${idCreator(`${tableName}-${rowIndex}-${tableIndex}`)}`}
                onClick={() => onAddColumn(tableName, rowIndex)}
                display="flex"
              >
                <TbPlus size={16} color="#228be6" />
              </UnstyledButton>
            </Flex>
          </Flex>
        }
      />

      {!children && (
        <>
          {columnEntries.map(([columnKey, columnSchema]) => (
            <SingleFundRow
              key={columnKey}
              columnSchema={columnSchema}
              cellSchema={schema.properties[columnKey]}
              levelIndex={levelIndex}
              value={columnValues[columnKey]}
              error={columnValues?.errors?.[columnKey]}
              onChange={value => handleCellChange(columnKey, value, null, rowIndex)}
              cellRefs={cellRefs}
              cellKey={`${keyPrefix}-${columnKey}-${rowIndex}`}
              onCellKeyDown={onCellKeyDown}
              showIndent={Boolean(level)}
              fundId={fund?.fund_id}
              fundName={fund?.fund_name}
              tableName={tableName}
              fieldName={columnKey}
              hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
              tableId={columnValues['id']}
              reportId={columnValues['report_id']}
              isFromStaticTemplate={columnValues?.['static_template_fields']?.includes(columnKey)}
              symbol={schema.properties[columnKey]?.symbol}
            />
          ))}
        </>
      )}
      {children &&
        children.map(childQuestion => (
          <Fragment key={childQuestion.level ?? childQuestion.name}>
            {renderSingleFundSection({
              question: childQuestion,
              schema,
              table,
              tableName,
              rowIndex,
              tableIndex,
              levelIndex: levelIndex + 1,
              totalTableCount,
              onAddColumn,
              onDeleteColumn,
              handleCellChange,
              questionIndex,
              cellRefs,
              onCellKeyDown,
              keyPrefix: childQuestion.level ?? childQuestion.name,
              showQuestionHeader,
              fund
            })}
          </Fragment>
        ))}
    </>
  );
};

export const MultipleTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  onAddColumn,
  onDeleteColumn,
  questionLevels,
  name,
  showQuestionHeader = true,
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const { classes } = useTableStyles();
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);
  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const visibleRows = useMemo(() => {
    const tables = funds?.[fundId]?.fund_data?.[tableName] || [];

    return tables.reduce((acc, table, index) => {
      if (!table?.is_deleted) {
        acc.push({ table, index });
      }
      return acc;
    }, []);
  }, [fundId, funds, tableName]);

  const handleCellChange = useCallback(
    (column, value, targetFundId, rowIndex) => {
      const resolvedFundId = isMultiFunds ? targetFundId : fundId;
      if (!resolvedFundId && resolvedFundId !== 0 && isMultiFunds) {
        return;
      }

      const source = funds?.[resolvedFundId]?.fund_data?.[tableName];
      if (!source) return;

      let nextFundState = [...source];
      if (!nextFundState[rowIndex]) return;

      nextFundState[rowIndex] = {
        ...nextFundState[rowIndex],
        [column]: value
      };

      if (column === 'month' && tableName === 'HEDGE_FUND_POSITIONS') {
        nextFundState = [
          ...nextFundState.filter(row => row.month).sort((a, b) => +a.month - +b.month),
          ...nextFundState.filter(row => !row.month)
        ];
      }

      onChangeFund(tableName, nextFundState, targetFundId);
    },
    [fundId, funds, isMultiFunds, onChangeFund, tableName]
  );

  const { questionNumber, questionName } = parseQuestionDisplayName(name);

  if (isMultiFunds) {
    const totalTableCount = visibleRows.length;
    const isWidthFit =
      fundKeys.length > 3 && window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH;
    return (
      <Flex
        id={`formpf-multiple-table-${idCreator(tableName)}-multi`}
        direction="column"
        w={isWidthFit ? 'fit-content' : 'auto'}
        bd={`1px solid ${BORDER_COLOR}`}
      >
        {showQuestionHeader && (
          <QuestionHeader
            id={`formpf-multiple-table-header-${idCreator(tableName)}-multi`}
            name={questionName}
            number={questionNumber}
            isMultiFunds={isMultiFunds}
            fundKeys={fundKeys}
            funds={funds}
            isOpen={isQuestionOpen}
            onToggle={onToggleQuestion}
          />
        )}
        {isQuestionOpen &&
          questionLevels.map((question, questionIndex) => {
            const keyPrefix = question.level ?? question.name;
            return (
              <Fragment key={`${questionIndex}-multi-view`}>
                {visibleRows.map(({ index }, displayIndex) => (
                  <Fragment key={`${question.level}-${index}-${questionIndex}`}>
                    {renderMultiFundsSection({
                      question,
                      schema,
                      funds,
                      fundKeys,
                      tableName,
                      rowIndex: index,
                      tableIndex: displayIndex + 1,
                      levelIndex: 0,
                      onAddColumn,
                      onDeleteColumn,
                      handleCellChange,
                      totalTableCount,
                      questionIndex,
                      cellRefs: { current: cellRefs, register: registerCell },
                      onCellKeyDown,
                      keyPrefix,
                      showQuestionHeader: displayIndex === 0,
                      classes
                    })}
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
      </Flex>
    );
  }

  const totalTableCount = visibleRows.length;
  return (
    <Flex
      id={`formpf-multiple-table-${idCreator(tableName)}-single`}
      direction="column"
      bd={`1px solid ${BORDER_COLOR}`}
    >
      {showQuestionHeader && (
        <QuestionHeader
          id={`formpf-multiple-table-header-${idCreator(tableName)}-single`}
          number={questionNumber}
          name={questionName}
          isMultiFunds={isMultiFunds}
          isOpen={isQuestionOpen}
          onToggle={onToggleQuestion}
        />
      )}
      {isQuestionOpen &&
        questionLevels.map((question, questionIndex) => {
          const keyPrefix = question.level ?? question.name;
          return (
            <Fragment key={`${question.level}-${questionIndex}`}>
              {visibleRows.map(({ table, index }, displayIndex) => (
                <Fragment key={`${question.level}-${index}-${questionIndex}`}>
                  {renderSingleFundSection({
                    question,
                    schema,
                    table,
                    tableName,
                    rowIndex: index,
                    tableIndex: displayIndex + 1,
                    levelIndex: 0,
                    totalTableCount,
                    onAddColumn,
                    onDeleteColumn,
                    handleCellChange,
                    questionIndex,
                    cellRefs: { current: cellRefs, register: registerCell },
                    onCellKeyDown,
                    keyPrefix,
                    showQuestionHeader: displayIndex === 0,
                    fund: funds?.[fundId]
                  })}
                </Fragment>
              ))}
            </Fragment>
          );
        })}
    </Flex>
  );
};
